import React, { useState, useEffect, useRef } from 'react';
import { formatDateDMY } from '../utils/dateFormat';
import Layout from '../components/Layout';
import { settingsAPI, staffAPI, authAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { resolveMediaUrl } from '../utils/mediaUrl';
import logo from '../logo.png';

// Must be defined OUTSIDE Settings to keep stable refs and prevent focus loss on keystrokes
const Field = ({ label, lbl, children }) => (
  <div><label style={lbl}>{label}</label>{children}</div>
);

const SaveRow = ({ onSave, saving, saved, acc }) => (
  <div style={{ marginTop: 24, display: 'flex', gap: 12, alignItems: 'center' }}>
    <button type="button" onClick={onSave} disabled={saving}
      style={{ padding: '10px 28px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
      {saving ? '⏳ Saving...' : '💾 Save Changes'}
    </button>
    {saved && <span style={{ color: '#10B981', fontWeight: 700, fontSize: 14 }}>✓ {saved}!</span>}
  </div>
);

// Invoice number format options
const INV_FORMATS = [
  { id: 'date',      icon: '📅', label: 'Date Based',        desc: 'Resets daily — best for high volume' },
  { id: 'yearmonth', icon: '📆', label: 'Month Based',       desc: 'Resets each month' },
  { id: 'year',      icon: '🗓️', label: 'Year Based',        desc: 'Resets each year' },
  { id: 'finyear',   icon: '📊', label: 'Financial Year',    desc: 'Apr–Mar cycle (India GST)' },
  { id: 'seq',       icon: '🔢', label: 'Simple Sequential', desc: 'Never resets — global count' },
];

const buildInvSample = (prefix, fmt) => {
  const p = (prefix || 'INV').toUpperCase();
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const fy = (now.getMonth() + 1) < 4
    ? `${String(y - 1).slice(2)}-${String(y).slice(2)}`
    : `${String(y).slice(2)}-${String(y + 1).slice(2)}`;
  switch (fmt) {
    case 'yearmonth': return `${p}-${y}-${m}-001`;
    case 'year':      return `${p}-${y}-0001`;
    case 'finyear':   return `${p}/${fy}/0001`;
    case 'seq':       return `${p}-00001`;
    default:          return `${p}-${y}${m}${d}-0001`;
  }
};

const Settings = () => {
  const { darkMode, toggleDark, colorKey, setColor, color, themes } = useTheme();
  const { loginRole } = useAuth();
  const isOwner = loginRole === 'owner' || loginRole === 'client';
  const [activeTab, setActiveTab] = useState('shop');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef();
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const signatureInputRef = useRef();
  const autoSaveTimer = useRef(null);
  const loadingRef = useRef(true);

  useEffect(() => {
    return () => { if (logoPreview) URL.revokeObjectURL(logoPreview); if (signaturePreview) URL.revokeObjectURL(signaturePreview); };
  }, [logoPreview, signaturePreview]);
  const [autoSaved, setAutoSaved] = useState('');
  const [gdriveConfig, setGdriveConfig] = useState({ client_id: '', client_secret: '', folder_id: '' });
  const [gdriveStatus, setGdriveStatus] = useState({ connected: false, credentials_set: false, last_backup: null, folder_id: '', redirect_uri: '', gdrive_client_id_full: '', gdrive_client_secret_full: '' });
  const [gdriveLoading, setGdriveLoading] = useState(false);
  const [gdriveMsg, setGdriveMsg] = useState('');
  // Backup & Restore state
  const [backupList, setBackupList] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [fileDownloading, setFileDownloading] = useState({});
  const [backupMsg, setBackupMsg] = useState('');
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  // Staff management state
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffMsg, setStaffMsg] = useState('');
  const [staffForm, setStaffForm] = useState({ name: '', email: '', password: '', role: 'staff', permissions: {} });
  const [editingStaff, setEditingStaff] = useState(null);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [resetPwStaff, setResetPwStaff] = useState(null);
  const [newStaffPw, setNewStaffPw] = useState('');
  const PERM_MODULES = ['dashboard', 'customers', 'products', 'invoices', 'suppliers', 'quotations', 'settings'];
  const PERM_ACTIONS = ['read', 'write', 'delete'];
  // Password change state
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [form, setForm] = useState({
    shop_name: '', shop_phone: '', shop_address: '', shop_email: '', gst_number: '', owner_name: '',
    logo_url: '',
    signature_url: '',
    bank_name: '', bank_account: '', bank_account_name: '', bank_ifsc: '', bank_branch: '', upi_id: '',
    terms_conditions: 'Thank you for your business!\n\n* Goods once sold will not be taken back or exchanged.\n* Payment is due immediately unless otherwise agreed.\n* Disputes subject to local jurisdiction.',
    telegram_bot_token: '', telegram_chat_id: '', telegram_enabled: false,
    smtp_host: '', smtp_port: 587, smtp_email: '', smtp_password: '', email_enabled: false,
    print_format: 'classic',
    page_size: 'A4', page_orientation: 'portrait',
    margin_top: 10, margin_bottom: 10, margin_left: 10, margin_right: 10,
    invoice_prefix: 'INV', invoice_format: 'date',
    business_type: 'retail', product_field_config: {}, default_gst_percent: 0, default_unit: 'Pcs', default_category: '', state: 'Maharashtra', composition_scheme: false,
  });

  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  const loadGdriveStatus = () => {
    settingsAPI.gdriveStatus().then(r => {
      const d = r.data || {};
      setGdriveStatus({
        connected: !!d.connected,
        credentials_set: !!d.credentials_set,
        last_backup: d.last_backup,
        folder_id: d.folder_id || '',
        redirect_uri: d.redirect_uri || '',
        gdrive_client_id_full: d.gdrive_client_id_full || '',
        gdrive_client_secret_full: d.gdrive_client_secret_full || '',
      });
      setGdriveConfig(c => ({
        ...c,
        client_id: d.gdrive_client_id_full || c.client_id,
        client_secret: d.gdrive_client_secret_full || c.client_secret,
        folder_id: d.folder_id || c.folder_id,
      }));
    }).catch(() => {});
  };

  // Load staff when staff tab is opened
  useEffect(() => {
    if (activeTab === 'staff' && isOwner && staffList.length === 0) loadStaff();
    if (activeTab === 'backup') loadBackupList();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    settingsAPI.get().then(r => {
      const d = r.data || {};
      setForm(f => ({
        ...f,
        shop_name: d.shop_name || '', shop_phone: d.shop_phone || '', shop_address: d.shop_address || '',
        shop_email: d.shop_email || '', gst_number: d.gst_number || '', owner_name: d.owner_name || '',
        logo_url: d.logo_url || '',
        signature_url: d.signature_url || '',
        bank_name: d.bank_name || '', bank_account: d.bank_account || '',
        bank_account_name: d.bank_account_name || '', bank_ifsc: d.bank_ifsc || '',
        bank_branch: d.bank_branch || '', upi_id: d.upi_id || '',
        terms_conditions: d.terms_conditions || f.terms_conditions,
        telegram_bot_token: d.telegram_bot_token || '', telegram_chat_id: d.telegram_chat_id || '',
        telegram_enabled: !!d.telegram_enabled,
        smtp_host: d.smtp_host || '', smtp_port: d.smtp_port || 587,
        smtp_email: d.smtp_email || '', smtp_password: d.smtp_password || '',
        email_enabled: !!d.email_enabled,
        print_format: d.print_format || 'classic',
        page_size: d.page_size || 'A4',
        page_orientation: d.page_orientation || 'portrait',
        margin_top: d.margin_top ?? 10,
        margin_bottom: d.margin_bottom ?? 10,
        margin_left: d.margin_left ?? 10,
        margin_right: d.margin_right ?? 10,
        invoice_prefix: d.invoice_prefix || 'INV',
        invoice_format: d.invoice_format || 'date',
        business_type: d.business_type || 'retail',
        product_field_config: (() => { try { return (typeof d.product_field_config === 'string' ? JSON.parse(d.product_field_config) : d.product_field_config) || {}; } catch { return {}; } })(),
        default_gst_percent: d.default_gst_percent ?? 0,
        default_unit: d.default_unit || 'Pcs',
        default_category: d.default_category || '',
        state: d.state || 'Maharashtra',
        composition_scheme: !!d.composition_scheme,
      }));
      // Restore GDrive credential inputs from DB so they persist across page refreshes.
      // gdrive_client_id is returned as-is; gdrive_client_secret is masked as "****" if set.
      setGdriveConfig(c => ({
        client_id: d.gdrive_client_id || c.client_id,
        client_secret: d.gdrive_client_secret ? '****' : c.client_secret,
        folder_id: d.gdrive_folder_id || c.folder_id,
      }));
    }).catch(() => {}).finally(() => { setLoading(false); loadGdriveStatus(); loadingRef.current = false; });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save non-sensitive fields after 2.5s of no changes
  useEffect(() => {
    if (loadingRef.current) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        await settingsAPI.update({
          shop_name: form.shop_name, shop_phone: form.shop_phone, shop_email: form.shop_email,
          gst_number: form.gst_number, shop_address: form.shop_address, owner_name: form.owner_name,
          bank_name: form.bank_name, bank_account: form.bank_account,
          bank_account_name: form.bank_account_name, bank_ifsc: form.bank_ifsc,
          bank_branch: form.bank_branch, upi_id: form.upi_id,
          terms_conditions: form.terms_conditions,
          invoice_prefix: form.invoice_prefix, invoice_format: form.invoice_format,
          print_format: form.print_format,
          page_size: form.page_size,
          page_orientation: form.page_orientation,
          margin_top: parseFloat(form.margin_top) || 10,
          margin_bottom: parseFloat(form.margin_bottom) || 10,
          margin_left: parseFloat(form.margin_left) || 10,
          margin_right: parseFloat(form.margin_right) || 10,
        });
        setAutoSaved('✓ Auto-saved');
        setTimeout(() => setAutoSaved(''), 3000);
      } catch (err) { console.error('Auto-save failed:', err); setAutoSaved('✗ Auto-save failed'); }
    }, 2500);
    return () => clearTimeout(autoSaveTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.shop_name, form.shop_phone, form.shop_email, form.gst_number, form.shop_address, form.owner_name,
      form.bank_name, form.bank_account, form.bank_account_name, form.bank_ifsc, form.bank_branch, form.upi_id,
      form.terms_conditions, form.invoice_prefix, form.invoice_format, form.print_format, form.page_size, form.page_orientation, form.margin_top, form.margin_bottom, form.margin_left, form.margin_right]);

  const saveGdriveConfig = async () => {
    setGdriveLoading(true); setGdriveMsg('');
    try {
      await settingsAPI.gdriveUpdateConfig({ gdrive_client_id: gdriveConfig.client_id, gdrive_client_secret: gdriveConfig.client_secret, gdrive_folder_id: gdriveConfig.folder_id });
      setGdriveMsg('✅ Credentials saved! Now connect your Google Drive.');
      loadGdriveStatus();
    } catch (e) { setGdriveMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setGdriveLoading(false);
  };

  const connectGDrive = async () => {
    setGdriveLoading(true); setGdriveMsg('Opening Google sign-in…');
    try {
      const r = await settingsAPI.gdriveAuthUrl();
      const popup = window.open(r.data.auth_url, 'gdrive_auth', 'width=600,height=700,menubar=no,toolbar=no');
      if (!popup) { setGdriveMsg('❌ Popup blocked — please allow popups for this site.'); setGdriveLoading(false); return; }
      const handler = async (e) => {
        if (e.data?.type !== 'gdrive_code') return;
        window.removeEventListener('message', handler);
        popup?.close();
        try {
          await settingsAPI.gdriveExchangeCode(e.data.code);
          setGdriveMsg('✅ Google Drive connected successfully!');
          loadGdriveStatus();
        } catch (err) { setGdriveMsg('❌ ' + (err.response?.data?.detail || err.message)); }
      };
      window.addEventListener('message', handler);
    } catch (e) { setGdriveMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setGdriveLoading(false);
  };

  const disconnectGDrive = async () => {
    if (!window.confirm('Disconnect Google Drive? Daily backups will stop.')) return;
    setGdriveLoading(true); setGdriveMsg('');
    try {
      await settingsAPI.gdriveDisconnect();
      setGdriveMsg('Google Drive disconnected.');
      loadGdriveStatus();
    } catch (e) { setGdriveMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setGdriveLoading(false);
  };

  const runBackupNow = async () => {
    setGdriveLoading(true); setGdriveMsg('Running backup…');
    try {
      await settingsAPI.gdriveBackupNow();
      setGdriveMsg('✅ Backup completed successfully!');
      loadGdriveStatus();
    } catch (e) { setGdriveMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setGdriveLoading(false);
  };

  // ── Local Backup & Restore functions ──
  const loadBackupList = async () => {
    try {
      const r = await settingsAPI.listBackups();
      setBackupList(r.data?.backups || []);
    } catch (e) {
      console.error('Failed to load backups:', e);
      setBackupMsg('❌ Failed to load backup list: ' + (e.response?.data?.detail || e.message));
    }
  };

  const downloadBackupNow = async () => {
    setBackupLoading(true); setBackupMsg('');
    try {
      const r = await settingsAPI.downloadBackupNow();
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `backup_${ts}.json.gz`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setBackupMsg('✅ Backup downloaded successfully!');
      loadBackupList();
    } catch (e) { setBackupMsg('❌ Download failed: ' + (e.response?.data?.detail || e.message)); }
    setBackupLoading(false);
  };

  const downloadBackupFile = async (filename) => {
    setFileDownloading(prev => ({ ...prev, [filename]: true }));
    setBackupMsg('');
    try {
      const r = await settingsAPI.downloadBackupFile(filename);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) { setBackupMsg('❌ Download failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setFileDownloading(prev => ({ ...prev, [filename]: false })); }
  };

  const handleRestoreUpload = (e) => {
    const f = e.target.files?.[0];
    if (f) { setRestoreFile(f); setShowRestoreConfirm(true); }
  };

  const executeRestore = async () => {
    if (!restoreFile) return;
    setShowRestoreConfirm(false);
    setRestoring(true); setBackupMsg('');
    try {
      const fd = new FormData();
      fd.append('file', restoreFile);
      const r = await settingsAPI.restoreBackup(fd);
      const total = r.data?.summary?.total_rows_restored || 0;
      setBackupMsg(`✅ Restore completed! ${total} rows restored across all tables.`);
      setRestoreFile(null);
      loadBackupList();
    } catch (e) { setBackupMsg('❌ Restore failed: ' + (e.response?.data?.detail || e.message)); }
    setRestoring(false);
  };

  const cancelRestore = () => {
    setShowRestoreConfirm(false); setRestoreFile(null);
  };

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // ── Staff management functions ──
  const loadStaff = async () => {
    setStaffLoading(true);
    try {
      const r = await staffAPI.list();
      setStaffList(r.data.staff || r.data || []);
    } catch (e) { setStaffMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setStaffLoading(false);
  };

  const saveStaff = async () => {
    setStaffMsg('');
    if (!staffForm.name || !staffForm.email) { setStaffMsg('❌ Name and email are required'); return; }
    if (!editingStaff && !staffForm.password) { setStaffMsg('❌ Password is required for new staff'); return; }
    setStaffLoading(true);
    try {
      if (editingStaff) {
        await staffAPI.update(editingStaff.id, { name: staffForm.name, email: staffForm.email, role: staffForm.role, permissions: staffForm.permissions });
        setStaffMsg('✅ Staff updated!');
      } else {
        await staffAPI.create({ name: staffForm.name, email: staffForm.email, password: staffForm.password, role: staffForm.role, permissions: staffForm.permissions });
        setStaffMsg('✅ Staff member added!');
      }
      setShowStaffForm(false);
      setEditingStaff(null);
      setStaffForm({ name: '', email: '', password: '', role: 'staff', permissions: {} });
      loadStaff();
    } catch (e) { setStaffMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setStaffLoading(false);
  };

  const deleteStaff = async (id, name) => {
    if (!window.confirm(`Delete staff member "${name}"? This cannot be undone.`)) return;
    setStaffLoading(true);
    try {
      await staffAPI.delete(id);
      setStaffMsg('✅ Staff deleted');
      loadStaff();
    } catch (e) { setStaffMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setStaffLoading(false);
  };

  const resetStaffPassword = async () => {
    if (!newStaffPw || newStaffPw.length < 6) { setStaffMsg('❌ Password must be at least 6 characters'); return; }
    setStaffLoading(true);
    try {
      await staffAPI.resetPassword(resetPwStaff.id, newStaffPw);
      setStaffMsg('✅ Password reset successfully');
      setResetPwStaff(null);
      setNewStaffPw('');
    } catch (e) { setStaffMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setStaffLoading(false);
  };

  const togglePerm = (mod, action) => {
    setStaffForm(f => {
      const perms = { ...f.permissions };
      if (!perms[mod]) perms[mod] = [];
      const arr = [...perms[mod]];
      const idx = arr.indexOf(action);
      if (idx >= 0) arr.splice(idx, 1); else arr.push(action);
      perms[mod] = arr;
      return { ...f, permissions: perms };
    });
  };

  const save = async (fields, label) => {
    setSaving(true); setSaved('');
    try {
      await settingsAPI.update(fields);
      setSaved(label || 'Saved');
      setTimeout(() => setSaved(''), 3000);
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image too large. Please upload a file under 2 MB.');
      e.target.value = '';
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      alert('Unsupported format. Please use PNG, JPG, GIF or WebP.');
      e.target.value = '';
      return;
    }
    setLogoPreview(URL.createObjectURL(file));
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await settingsAPI.uploadLogo(fd);
      setForm(f => ({ ...f, logo_url: res.data.logo_url }));
      setSaved('Logo Uploaded');
      setTimeout(() => setSaved(''), 3000);
    } catch (e) { alert('Logo upload failed: ' + (e.response?.data?.detail || e.message)); }
    setLogoUploading(false);
  };

  const logoSrc = logoPreview || (form.logo_url ? resolveMediaUrl(form.logo_url) : logo);
  const signatureSrc = signaturePreview || (form.signature_url ? resolveMediaUrl(form.signature_url) : null);

  const handleRemoveLogo = async () => {
    if (!window.confirm('Remove shop logo?')) return;
    setLogoPreview(null);
    setForm(f => ({ ...f, logo_url: '' }));
    await save({ logo_url: '' }, 'Logo Removed');
  };

  const handleRemoveSignature = async () => {
    if (!window.confirm('Remove shop signature?')) return;
    setSignaturePreview(null);
    setForm(f => ({ ...f, signature_url: '' }));
    await save({ signature_url: '' }, 'Signature Removed');
  };

  const handleSignatureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image too large. Please upload a file under 2 MB.');
      e.target.value = '';
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      alert('Unsupported format. Please use PNG, JPG, GIF or WebP.');
      e.target.value = '';
      return;
    }
    setSignaturePreview(URL.createObjectURL(file));
    setSignatureUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await settingsAPI.uploadSignature(fd);
      setForm(f => ({ ...f, signature_url: res.data.signature_url }));
      setSaved('Signature Uploaded');
      setTimeout(() => setSaved(''), 3000);
    } catch (e) { alert('Signature upload failed: ' + (e.response?.data?.detail || e.message)); }
    setSignatureUploading(false);
  };

  const tabs = [
    { id: 'shop', label: '🏪 Shop Info' },
    { id: 'business', label: '🏢 Business' },
    { id: 'bank', label: '🏦 Bank Details' },
    { id: 'terms', label: '📋 Terms' },
    { id: 'invoice', label: '🔢 Invoice Series' },
    ...(isOwner ? [{ id: 'staff', label: '👥 Staff' }] : []),
    { id: 'telegram', label: '💬 Telegram' },
    { id: 'email', label: '📧 Email / SMTP' },
    { id: 'print', label: '🖨️ Print Format' },
    { id: 'backup', label: '☁️ Backup' },
    { id: 'password', label: '🔒 Password' },
    { id: 'theme', label: '🎨 Theme' },
  ];

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <h1 style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>Settings</h1>
            {autoSaved && <span style={{ color: '#10B981', fontWeight: 700, fontSize: 13, background: darkMode ? '#064e3b22' : '#d1fae5', padding: '4px 12px', borderRadius: 20 }}>{autoSaved}</span>}
          </div>
          <p style={{ ...muted, margin: '4px 0 0', fontSize: 14 }}>Manage shop info, bank details, notifications and more</p>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 24, background: darkMode ? '#0F172A' : '#F1F5F9', padding: 4, borderRadius: 12, overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ flex: 1, padding: '9px 10px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', transition: 'all 0.2s',
                background: activeTab === t.id ? (darkMode ? '#1E293B' : '#fff') : 'transparent',
                color: activeTab === t.id ? acc : (darkMode ? '#64748B' : '#94A3B8'),
                boxShadow: activeTab === t.id ? '0 1px 6px rgba(0,0,0,0.12)' : 'none' }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: darkMode ? '#64748B' : '#94A3B8', fontSize: 16 }}>Loading settings…</div>
        ) : (
          <>
            {/* ── Shop Info ── */}
            {activeTab === 'shop' && (
              <div style={{ ...card, padding: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <img
                      src={logoSrc}
                      alt="Logo"
                      onError={(e) => {
                        e.currentTarget.src = logo;
                      }}
                      style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover', border: `2px solid ${acc}40`, background: '#fff' }}
                    />
                    <label htmlFor="logo-upload" title="Change logo" style={{ position: 'absolute', bottom: -4, right: -4, background: acc, color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>✎</label>
                    <input id="logo-upload" ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }} onChange={handleLogoChange} />
                    {logoUploading && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>⏳</div>}
                  </div>
                  <div>
                    <div style={{ ...pg, fontWeight: 800, fontSize: 18 }}>{form.shop_name || 'Your Shop'}</div>
                    <div style={{ ...muted, fontSize: 12 }}>{form.shop_phone || 'No phone set'} · {form.gst_number || 'No GSTIN'}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                      <label htmlFor="logo-upload" style={{ color: acc, fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                        Click ✎ to change logo
                      </label>
                      {form.logo_url && (
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                        >
                          ✕ Remove Logo
                        </button>
                      )}
                      <span style={{ ...muted, fontSize: 11 }}>PNG, JPG, GIF, WebP · Max 2 MB</span>
                    </div>
                  </div>
                </div>
                {/* Signature Upload */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    {signatureSrc ? (
                      <img
                        src={signatureSrc}
                        alt="Signature"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                        style={{ width: 120, height: 50, objectFit: 'contain', borderRadius: 8, border: `2px solid ${acc}40`, background: '#fff' }}
                      />
                    ) : (
                      <div style={{ width: 120, height: 50, borderRadius: 8, border: `2px dashed ${darkMode ? '#475569' : '#CBD5E1'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                        <span style={{ ...muted, fontSize: 11 }}>No Signature</span>
                      </div>
                    )}
                    <label htmlFor="signature-upload" title="Change signature" style={{ position: 'absolute', bottom: -4, right: -4, background: acc, color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>✎</label>
                    <input id="signature-upload" ref={signatureInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }} onChange={handleSignatureChange} />
                    {signatureUploading && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>⏳</div>}
                  </div>
                  <div>
                    <div style={{ ...pg, fontWeight: 700, fontSize: 14 }}>✍️ Authorised Signature</div>
                    <div style={{ ...muted, fontSize: 11, marginTop: 2 }}>Upload your signature image to appear on invoices & quotations</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                      <label htmlFor="signature-upload" style={{ color: acc, fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                        {form.signature_url ? 'Click ✎ to change signature' : 'Click ✎ to upload signature'}
                      </label>
                      {form.signature_url && (
                        <button
                          type="button"
                          onClick={handleRemoveSignature}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                        >
                          ✕ Remove Signature
                        </button>
                      )}
                      <span style={{ ...muted, fontSize: 11 }}>PNG, JPG, GIF, WebP · Max 2 MB</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
                  <Field lbl={lbl} label="Shop Name"><input style={inp} value={form.shop_name} onChange={set('shop_name')} placeholder="e.g. Sharma Traders" /></Field>
                  <Field lbl={lbl} label="Owner / Contact Person"><input style={inp} value={form.owner_name} onChange={set('owner_name')} placeholder="e.g. Rajesh Sharma" /></Field>
                  <Field lbl={lbl} label="Phone Number"><input style={inp} value={form.shop_phone} onChange={set('shop_phone')} placeholder="+91 99999 99999" /></Field>
                  <Field lbl={lbl} label="Email Address"><input style={{ ...inp }} type="email" value={form.shop_email} onChange={set('shop_email')} placeholder="shop@example.com" /></Field>
                  <Field lbl={lbl} label="GSTIN">
                    <input style={inp} value={form.gst_number} onChange={set('gst_number')} placeholder="27AABCU9603R1ZX" maxLength={15} />
                  </Field>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field lbl={lbl} label="Full Address">
                      <textarea style={{ ...inp, minHeight: 78, resize: 'vertical' }} value={form.shop_address} onChange={set('shop_address')} placeholder="Street, City, State, Pincode" />
                    </Field>
                  </div>
                </div>
                <SaveRow acc={acc} saving={saving} saved={saved} onSave={() => save({ shop_name: form.shop_name, shop_phone: form.shop_phone, shop_email: form.shop_email, gst_number: form.gst_number, shop_address: form.shop_address, owner_name: form.owner_name, logo_url: form.logo_url, signature_url: form.signature_url }, 'Shop Info Saved')} />
              </div>
            )}

            {/* ── Business Config ── */}
            {activeTab === 'business' && (
              <div style={{ ...card, padding: 28 }}>
                <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>🏢 Business Configuration</h3>
                <p style={{ ...muted, fontSize: 13, margin: '0 0 22px' }}>Configure your business type, default values, and which product fields you need.</p>

                {/* Business Type */}
                <div style={{ marginBottom: 24 }}>
                  <label style={lbl}>Business Type</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                    {[{v:'retail',l:'🛒 Retail',d:'Shop / Store'},{v:'wholesale',l:'📦 Wholesale',d:'Bulk dealer'},{v:'service',l:'🔧 Service',d:'Service provider'},{v:'manufacturing',l:'🏭 Manufacturing',d:'Maker / Producer'},{v:'restaurant',l:'🍽️ Restaurant',d:'Food & Beverages'},{v:'pharmacy',l:'💊 Pharmacy',d:'Medicine / Health'}].map(bt => (
                      <button key={bt.v} type="button" onClick={() => setForm(f => ({ ...f, business_type: bt.v }))}
                        style={{ flex: '1 1 140px', padding: '10px 12px', border: `2px solid ${form.business_type === bt.v ? acc : (darkMode ? '#334155' : '#CBD5E1')}`, borderRadius: 10, background: form.business_type === bt.v ? (darkMode ? `${acc}22` : `${acc}11`) : (darkMode ? '#0F172A' : '#F8FAFC'), cursor: 'pointer', textAlign: 'left' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: form.business_type === bt.v ? acc : (darkMode ? '#94A3B8' : '#64748B') }}>{bt.l}</div>
                        <div style={{ fontSize: 10, ...muted }}>{bt.d}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Defaults */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 18, marginBottom: 24 }}>
                  <Field lbl={lbl} label="Default GST %">
                    <select style={inp} value={form.default_gst_percent} onChange={e => setForm(f => ({ ...f, default_gst_percent: parseFloat(e.target.value) || 0 }))}>
                      {[0, 3, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </Field>
                  <Field lbl={lbl} label="Default Unit">
                    <select style={inp} value={form.default_unit} onChange={set('default_unit')}>
                      {['Pcs','Nos','Kg','Gm','Ltr','Ml','Mtr','Cm','Ft','Sqft','Box','Pack','Set','Pair','Roll','Sheet','Bag','Can','Btl','Doz'].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </Field>
                  <Field lbl={lbl} label="Default Category">
                    <input style={inp} value={form.default_category} onChange={set('default_category')} placeholder="e.g. General" />
                  </Field>
                  <Field lbl={lbl} label="State (for GST)">
                    <select style={inp} value={form.state} onChange={set('state')}>
                      {['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Jammu & Kashmir','Ladakh','Chandigarh','Puducherry','Dadra & Nagar Haveli','Lakshadweep','Andaman & Nicobar'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>

                {/* Composition Scheme */}
                <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" id="comp-scheme" checked={form.composition_scheme} onChange={e => setForm(f => ({ ...f, composition_scheme: e.target.checked }))} style={{ width: 18, height: 18, accentColor: acc }} />
                  <label htmlFor="comp-scheme" style={{ ...pg, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>GST Composition Scheme</label>
                  <span style={{ ...muted, fontSize: 11 }}>(Turnover ≤ ₹1.5 Cr, flat tax rate)</span>
                </div>

                {/* Product Field Configuration */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ ...lbl, marginBottom: 12 }}>Product Fields Configuration</label>
                  <p style={{ ...muted, fontSize: 12, margin: '0 0 14px' }}>Choose which fields appear when adding products, and mark mandatory ones.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 8 }}>
                    {[
                      { key: 'sku', label: 'SKU / Barcode', group: 'Basic' },
                      { key: 'category', label: 'Category', group: 'Basic' },
                      { key: 'brand', label: 'Brand', group: 'Basic' },
                      { key: 'description', label: 'Description', group: 'Basic' },
                      { key: 'purchase_price', label: 'Purchase Price', group: 'Pricing' },
                      { key: 'mrp', label: 'MRP', group: 'Pricing' },
                      { key: 'wholesale_price', label: 'Wholesale Price', group: 'Pricing' },
                      { key: 'discount_percent', label: 'Discount %', group: 'Pricing' },
                      { key: 'hsn_sac', label: 'HSN / SAC Code', group: 'Tax' },
                      { key: 'gst_percent', label: 'GST %', group: 'Tax' },
                      { key: 'cess_percent', label: 'Cess %', group: 'Tax' },
                      { key: 'current_stock', label: 'Opening Stock', group: 'Stock' },
                      { key: 'low_stock_threshold', label: 'Low Stock Alert', group: 'Stock' },
                      { key: 'weight', label: 'Weight', group: 'Physical' },
                      { key: 'dimensions', label: 'Dimensions', group: 'Physical' },
                      { key: 'color', label: 'Color', group: 'Physical' },
                      { key: 'size', label: 'Size', group: 'Physical' },
                      { key: 'warranty_months', label: 'Warranty', group: 'Advanced' },
                      { key: 'manufacturer', label: 'Manufacturer', group: 'Advanced' },
                      { key: 'batch_number', label: 'Batch Number', group: 'Advanced' },
                      { key: 'expiry_date', label: 'Expiry Date', group: 'Advanced' },
                    ].map(f => {
                      const cfg = form.product_field_config[f.key] || {};
                      return (
                        <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                          <input type="checkbox" checked={cfg.enabled !== false} onChange={e => {
                            const pfc = { ...form.product_field_config };
                            pfc[f.key] = { ...pfc[f.key], enabled: e.target.checked };
                            if (!e.target.checked) pfc[f.key].mandatory = false;
                            setForm(fm => ({ ...fm, product_field_config: pfc }));
                          }} style={{ width: 16, height: 16, accentColor: acc }} />
                          <span style={{ ...pg, fontSize: 13, flex: 1 }}>{f.label}</span>
                          <span style={{ fontSize: 10, ...muted, marginRight: 4 }}>{f.group}</span>
                          <label style={{ fontSize: 10, color: (cfg.mandatory ? '#dc2626' : (darkMode ? '#475569' : '#CBD5E1')), fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                            <input type="checkbox" checked={!!cfg.mandatory} disabled={cfg.enabled === false} onChange={e => {
                              const pfc = { ...form.product_field_config };
                              pfc[f.key] = { ...pfc[f.key], mandatory: e.target.checked };
                              setForm(fm => ({ ...fm, product_field_config: pfc }));
                            }} style={{ width: 12, height: 12, accentColor: '#dc2626' }} />
                            Required
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <SaveRow acc={acc} saving={saving} saved={saved} onSave={() => save({ business_type: form.business_type, product_field_config: form.product_field_config, default_gst_percent: form.default_gst_percent, default_unit: form.default_unit, default_category: form.default_category, state: form.state, composition_scheme: form.composition_scheme }, 'Business Config Saved')} />
              </div>
            )}

            {/* ── Bank Details ── */}
            {activeTab === 'bank' && (
              <div style={{ ...card, padding: 28 }}>
                <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>🏦 Bank & Payment Details</h3>
                <p style={{ ...muted, fontSize: 13, margin: '0 0 22px' }}>These details will appear on your printed invoices so customers can make payments easily.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
                  <Field lbl={lbl} label="Bank Name"><input style={inp} value={form.bank_name} onChange={set('bank_name')} placeholder="State Bank of India" /></Field>
                  <Field lbl={lbl} label="Account Holder Name"><input style={inp} value={form.bank_account_name} onChange={set('bank_account_name')} placeholder="Shop / Owner Name" /></Field>
                  <Field lbl={lbl} label="Account Number"><input style={inp} value={form.bank_account} onChange={set('bank_account')} placeholder="12345678901234" /></Field>
                  <Field lbl={lbl} label="IFSC Code"><input style={inp} value={form.bank_ifsc} onChange={set('bank_ifsc')} placeholder="SBIN0001234" /></Field>
                  <Field lbl={lbl} label="Branch"><input style={inp} value={form.bank_branch} onChange={set('bank_branch')} placeholder="Main Branch, City" /></Field>
                  <Field lbl={lbl} label="UPI ID"><input style={inp} value={form.upi_id} onChange={set('upi_id')} placeholder="yourshop@upi" /></Field>
                </div>
                {(form.bank_name || form.bank_account) && (
                  <div style={{ marginTop: 20, padding: '14px 18px', background: darkMode ? '#0F172A' : '#eff6ff', borderRadius: 10, border: `1px solid ${acc}40`, fontSize: 13 }}>
                    <div style={{ fontWeight: 700, color: acc, marginBottom: 6 }}>Preview on Invoice:</div>
                    <span style={{ color: darkMode ? '#F1F5F9' : '#0F172A' }}>
                      {form.bank_name && `Bank: ${form.bank_name}  `}{form.bank_account && `A/c: ${form.bank_account}  `}{form.bank_ifsc && `IFSC: ${form.bank_ifsc}  `}{form.upi_id && `UPI: ${form.upi_id}`}
                    </span>
                  </div>
                )}
                <SaveRow acc={acc} saving={saving} saved={saved} onSave={() => save({ bank_name: form.bank_name, bank_account: form.bank_account, bank_account_name: form.bank_account_name, bank_ifsc: form.bank_ifsc, bank_branch: form.bank_branch, upi_id: form.upi_id }, 'Bank Details Saved')} />
              </div>
            )}

            {/* ── Terms & Conditions ── */}
            {activeTab === 'terms' && (
              <div style={{ ...card, padding: 28 }}>
                <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>📋 Terms & Conditions</h3>
                <p style={{ ...muted, fontSize: 13, margin: '0 0 22px' }}>These terms appear at the bottom of every printed invoice. Use plain text, one term per line.</p>
                <Field lbl={lbl} label="Terms & Conditions">
                  <textarea style={{ ...inp, minHeight: 240, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.7 }}
                    value={form.terms_conditions} onChange={set('terms_conditions')}
                    placeholder="Enter your terms and conditions..." />
                </Field>
                <div style={{ marginTop: 16, padding: '12px 16px', background: darkMode ? '#0F172A' : '#f9fafb', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#9ca3af', marginBottom: 4, textTransform: 'uppercase' }}>Preview</div>
                  <div style={{ fontSize: 12, color: darkMode ? '#94A3B8' : '#6b7280', whiteSpace: 'pre-line', lineHeight: 1.6 }}>{form.terms_conditions}</div>
                </div>
                <SaveRow acc={acc} saving={saving} saved={saved} onSave={() => save({ terms_conditions: form.terms_conditions }, 'Terms Saved')} />
              </div>
            )}

            {/* ── Staff Management ── */}
            {activeTab === 'staff' && isOwner && (
              <div style={{ ...card, padding: 28 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: 0 }}>👥 Staff Management</h3>
                    <p style={{ ...muted, fontSize: 13, marginTop: 4 }}>Add, edit, and manage staff members and their permissions.</p>
                  </div>
                  <button onClick={() => { setShowStaffForm(true); setEditingStaff(null); setStaffForm({ name: '', email: '', password: '', role: 'staff', permissions: {} }); loadStaff(); }}
                    style={{ padding: '9px 20px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                    + Add Staff
                  </button>
                </div>

                {staffMsg && (
                  <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
                    background: staffMsg.startsWith('✅') ? (darkMode ? '#0d2b1e' : '#f0fdf4') : (darkMode ? '#2d1515' : '#fef2f2'),
                    color: staffMsg.startsWith('✅') ? (darkMode ? '#86efac' : '#15803d') : (darkMode ? '#fca5a5' : '#dc2626'),
                    border: `1px solid ${staffMsg.startsWith('✅') ? (darkMode ? '#16a34a44' : '#86efac') : (darkMode ? '#ef444444' : '#fca5a5')}` }}>
                    {staffMsg}
                  </div>
                )}

                {/* Staff Form (Add/Edit) */}
                {showStaffForm && (
                  <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 10, padding: 20, marginBottom: 20 }}>
                    <h4 style={{ ...pg, fontWeight: 700, marginBottom: 14 }}>{editingStaff ? '✏️ Edit Staff' : '➕ Add New Staff'}</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                      <Field lbl={lbl} label="Full Name"><input style={inp} value={staffForm.name} onChange={e => setStaffForm(f => ({ ...f, name: e.target.value }))} placeholder="Staff Name" /></Field>
                      <Field lbl={lbl} label="Email / Login ID"><input style={inp} type="email" value={staffForm.email} onChange={e => setStaffForm(f => ({ ...f, email: e.target.value }))} placeholder="staff@example.com" /></Field>
                      {!editingStaff && <Field lbl={lbl} label="Password"><input style={inp} type="password" value={staffForm.password} onChange={e => setStaffForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" /></Field>}
                      <Field lbl={lbl} label="Role">
                        <select style={inp} value={staffForm.role} onChange={e => setStaffForm(f => ({ ...f, role: e.target.value }))}>
                          <option value="staff">Staff</option>
                          <option value="manager">Manager</option>
                          <option value="accountant">Accountant</option>
                        </select>
                      </Field>
                    </div>

                    {/* Permissions Grid */}
                    <div style={{ marginTop: 18 }}>
                      <label style={lbl}>Module Permissions</label>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, color: darkMode ? '#94A3B8' : '#64748B', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Module</th>
                              {PERM_ACTIONS.map(a => (
                                <th key={a} style={{ textAlign: 'center', padding: '8px 10px', borderBottom: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, color: darkMode ? '#94A3B8' : '#64748B', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>{a}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {PERM_MODULES.map(mod => (
                              <tr key={mod}>
                                <td style={{ padding: '8px 10px', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, ...pg, fontWeight: 600, textTransform: 'capitalize' }}>{mod}</td>
                                {PERM_ACTIONS.map(act => (
                                  <td key={act} style={{ textAlign: 'center', padding: '8px 10px', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                                    <input type="checkbox" checked={(staffForm.permissions[mod] || []).includes(act)}
                                      onChange={() => togglePerm(mod, act)}
                                      style={{ width: 16, height: 16, cursor: 'pointer', accentColor: acc }} />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                      <button onClick={saveStaff} disabled={staffLoading}
                        style={{ padding: '9px 22px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                        {staffLoading ? '⏳ Saving…' : editingStaff ? '💾 Update Staff' : '💾 Add Staff'}
                      </button>
                      <button onClick={() => { setShowStaffForm(false); setEditingStaff(null); }}
                        style={{ padding: '9px 22px', background: 'transparent', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Reset Password Modal */}
                {resetPwStaff && (
                  <div style={{ background: darkMode ? '#0F172A' : '#FEF3C7', border: `1px solid ${darkMode ? '#475569' : '#FDE68A'}`, borderRadius: 10, padding: 18, marginBottom: 18 }}>
                    <h4 style={{ ...pg, fontWeight: 700, marginBottom: 10 }}>🔑 Reset Password for {resetPwStaff.name}</h4>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input style={{ ...inp, maxWidth: 260 }} type="password" value={newStaffPw} onChange={e => setNewStaffPw(e.target.value)} placeholder="New password (min 6 chars)" />
                      <button onClick={resetStaffPassword} disabled={staffLoading}
                        style={{ padding: '9px 18px', background: '#F59E0B', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                        {staffLoading ? '⏳' : '🔑 Reset'}
                      </button>
                      <button onClick={() => { setResetPwStaff(null); setNewStaffPw(''); }}
                        style={{ padding: '9px 18px', background: 'transparent', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Staff List */}
                {!staffList.length && !staffLoading && !showStaffForm ? (
                  <div style={{ textAlign: 'center', padding: 40, ...muted }}>
                    <p style={{ fontSize: 40, marginBottom: 8 }}>👥</p>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>No staff members yet</p>
                    <p style={{ fontSize: 13 }}>Click "Add Staff" to create your first staff account</p>
                    <button onClick={() => { loadStaff(); }} style={{ marginTop: 12, padding: '7px 16px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>🔄 Refresh</button>
                  </div>
                ) : (
                  <div>
                    {staffList.map(s => (
                      <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, flexWrap: 'wrap', gap: 10 }}>
                        <div>
                          <div style={{ ...pg, fontWeight: 700, fontSize: 14 }}>{s.name}
                            <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.status === 'active' ? (darkMode ? '#064E3B' : '#D1FAE5') : (darkMode ? '#7F1D1D' : '#FEE2E2'), color: s.status === 'active' ? (darkMode ? '#6EE7B7' : '#065F46') : (darkMode ? '#FCA5A5' : '#991B1B') }}>{s.status}</span>
                            <span style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: darkMode ? '#1E3A5F' : '#EFF6FF', color: darkMode ? '#60A5FA' : '#2563EB' }}>{s.role || 'staff'}</span>
                          </div>
                          <div style={{ ...muted, fontSize: 12, marginTop: 2 }}>{s.email}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => { setEditingStaff(s); setStaffForm({ name: s.name, email: s.email, password: '', role: s.role || 'staff', permissions: s.permissions || {} }); setShowStaffForm(true); }}
                            style={{ padding: '6px 12px', background: darkMode ? '#1E3A5F' : '#EFF6FF', color: darkMode ? '#60A5FA' : '#2563EB', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>✏️ Edit</button>
                          <button onClick={() => { setResetPwStaff(s); setNewStaffPw(''); }}
                            style={{ padding: '6px 12px', background: darkMode ? '#78350F44' : '#FEF3C7', color: darkMode ? '#FCD34D' : '#92400E', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>🔑</button>
                          <button onClick={() => deleteStaff(s.id, s.name)}
                            style={{ padding: '6px 12px', background: darkMode ? '#7F1D1D44' : '#FEE2E2', color: darkMode ? '#FCA5A5' : '#DC2626', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                        </div>
                      </div>
                    ))}
                    <button onClick={loadStaff} disabled={staffLoading}
                      style={{ marginTop: 14, padding: '7px 16px', background: 'transparent', color: acc, border: `1px solid ${acc}`, borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                      {staffLoading ? '⏳ Loading…' : '🔄 Refresh List'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Telegram ── */}
            {activeTab === 'telegram' && (
              <div style={{ ...card, padding: 28 }}>
                <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>💬 Telegram Notifications</h3>
                <p style={{ ...muted, fontSize: 13, margin: '0 0 22px' }}>Send automated messages and invoices via Telegram bot.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field lbl={lbl} label="Bot Token">
                      <input style={inp} type="password" value={form.telegram_bot_token} onChange={set('telegram_bot_token')} placeholder="1234567890:ABCdefGHIjklMNO..." />
                    </Field>
                  </div>
                  <Field lbl={lbl} label="Chat / Channel ID">
                    <input style={inp} value={form.telegram_chat_id} onChange={set('telegram_chat_id')} placeholder="-1001234567890" />
                  </Field>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 18 }}>
                    <input type="checkbox" id="tgEnable" checked={form.telegram_enabled} onChange={set('telegram_enabled')} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                    <label htmlFor="tgEnable" style={{ ...pg, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Enable Telegram Notifications</label>
                  </div>
                </div>
                <SaveRow acc={acc} saving={saving} saved={saved} onSave={() => save({ telegram_bot_token: form.telegram_bot_token, telegram_chat_id: form.telegram_chat_id, telegram_enabled: form.telegram_enabled }, 'Telegram Saved')} />
              </div>
            )}

            {/* ── Email / SMTP ── */}
            {activeTab === 'email' && (
              <div style={{ ...card, padding: 28 }}>
                <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>📧 Email (SMTP)</h3>
                <p style={{ ...muted, fontSize: 13, margin: '0 0 22px' }}>Configure SMTP to send invoices by email. Gmail: use App Password with smtp.gmail.com:587.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
                  <Field lbl={lbl} label="SMTP Host"><input style={inp} value={form.smtp_host} onChange={set('smtp_host')} placeholder="smtp.gmail.com" /></Field>
                  <Field lbl={lbl} label="SMTP Port"><input style={inp} type="number" value={form.smtp_port} onChange={set('smtp_port')} placeholder="587" /></Field>
                  <Field lbl={lbl} label="From Email"><input style={inp} type="email" value={form.smtp_email} onChange={set('smtp_email')} placeholder="your@gmail.com" /></Field>
                  <Field lbl={lbl} label="Password / App Password"><input style={inp} type="password" value={form.smtp_password} onChange={set('smtp_password')} placeholder="••••••••" /></Field>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" id="emailEnable" checked={form.email_enabled} onChange={set('email_enabled')} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                    <label htmlFor="emailEnable" style={{ ...pg, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Enable Email Notifications</label>
                  </div>
                </div>
                <SaveRow acc={acc} saving={saving} saved={saved} onSave={() => save({ smtp_host: form.smtp_host, smtp_port: parseInt(form.smtp_port) || 587, smtp_email: form.smtp_email, smtp_password: form.smtp_password, email_enabled: form.email_enabled }, 'Email Config Saved')} />
              </div>
            )}

            {/* ── Invoice Series ── */}
            {activeTab === 'invoice' && (
              <div style={{ ...card, padding: 28 }}>
                <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>🔢 Invoice Series &amp; Numbering</h3>
                <p style={{ ...muted, fontSize: 13, margin: '0 0 24px' }}>Choose how invoice numbers are generated for your business. The format applies to all new invoices created from today.</p>

                {/* Prefix */}
                <div style={{ marginBottom: 26 }}>
                  <label style={lbl}>Invoice Prefix <span style={{ ...muted, fontWeight: 400 }}>(max 8 chars)</span></label>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      style={{ ...inp, width: 160, textTransform: 'uppercase', fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}
                      value={form.invoice_prefix}
                      maxLength={8}
                      placeholder="INV"
                      onChange={e => setForm(f => ({ ...f, invoice_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9\-\/]/g, '').slice(0, 8) }))}
                    />
                    <span style={{ ...muted, fontSize: 12 }}>e.g.&nbsp; INV &nbsp;·&nbsp; TAX &nbsp;·&nbsp; BILL &nbsp;·&nbsp; QT &nbsp;·&nbsp; SV</span>
                  </div>
                </div>

                {/* Format selector */}
                <label style={{ ...lbl, marginBottom: 10, display: 'block' }}>Number Format</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 26 }}>
                  {INV_FORMATS.map(f => (
                    <button key={f.id} type="button"
                      onClick={() => setForm(prev => ({ ...prev, invoice_format: f.id }))}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
                        padding: '12px 16px',
                        border: `2px solid ${form.invoice_format === f.id ? acc : (darkMode ? '#334155' : '#CBD5E1')}`,
                        borderRadius: 10,
                        background: form.invoice_format === f.id ? (darkMode ? '#1e3a5f' : '#eff6ff') : (darkMode ? '#0F172A' : '#F8FAFC'),
                        cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: form.invoice_format === f.id ? acc : (darkMode ? '#F1F5F9' : '#0F172A') }}>{f.icon} {f.label}</span>
                        <span style={{ ...muted, fontSize: 12 }}>{f.desc}</span>
                      </div>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: form.invoice_format === f.id ? acc : (darkMode ? '#94A3B8' : '#475569'), whiteSpace: 'nowrap' }}>
                        {buildInvSample(form.invoice_prefix || 'INV', f.id)}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Live preview */}
                <div style={{ padding: '14px 18px', background: darkMode ? '#0F172A' : '#f0fdf4', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#bbf7d0'}`, marginBottom: 24 }}>
                  <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#64748B', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Next invoice will look like</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 800, color: acc }}>
                    {buildInvSample(form.invoice_prefix || 'INV', form.invoice_format)}
                  </div>
                </div>

                <SaveRow acc={acc} saving={saving} saved={saved}
                  onSave={() => save({
                    invoice_prefix: (form.invoice_prefix || 'INV').toUpperCase().trim(),
                    invoice_format: form.invoice_format,
                  }, 'Invoice Series Saved')} />
              </div>
            )}

            {/* ── Print Format ── */}
            {activeTab === 'print' && (
              <div style={{ ...card, padding: 28 }}>
                <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>🖨️ Default Print Format</h3>
                <p style={{ ...muted, fontSize: 13, margin: '0 0 22px' }}>Choose the default invoice format used when printing. You can still change it per-invoice at print time.</p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 28 }}>
                  {[
                    { id: 'classic', icon: '📄', title: 'A4 Classic', desc: 'Professional layout with CGST/SGST table, amount in words, bank details & signature. Best for GST invoices, quotations.' },
                    { id: 'modern', icon: '🎨', title: 'Modern Color', desc: 'Bold gradient header, colorful design on A4. Great for retail invoices and quotes with a premium feel.' },
                    { id: 'thermal', icon: '🖨️', title: 'Thermal Receipt', desc: 'Compact 80mm receipt layout for thermal printers. Fast, minimal, perfect for POS counters.' },
                  ].map(f => (
                    <button key={f.id} type="button" onClick={() => setForm(prev => ({ ...prev, print_format: f.id }))}
                      style={{ padding: '18px 16px', border: `2px solid ${form.print_format === f.id ? acc : (darkMode ? '#334155' : '#CBD5E1')}`, borderRadius: 12, background: form.print_format === f.id ? (darkMode ? '#1e3a5f' : '#eff6ff') : (darkMode ? '#0F172A' : '#F8FAFC'), cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>{f.icon}</div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: form.print_format === f.id ? acc : (darkMode ? '#F1F5F9' : '#0F172A'), marginBottom: 6 }}>{f.title}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.5, color: darkMode ? '#64748B' : '#64748B' }}>{f.desc}</div>
                      {form.print_format === f.id && (
                        <div style={{ marginTop: 10, display: 'inline-block', padding: '3px 10px', background: acc, color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>✓ Selected</div>
                      )}
                    </button>
                  ))}
                </div>

                <div style={{ padding: '14px 18px', background: darkMode ? '#0F172A' : '#f0fdf4', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#bbf7d0'}`, marginBottom: 20 }}>
                  <div style={{ fontSize: 13, color: darkMode ? '#86efac' : '#15803d' }}>
                    <strong>Current default:</strong> {form.print_format === 'classic' ? '📄 A4 Classic' : form.print_format === 'modern' ? '🎨 Modern Color' : '🖨️ Thermal Receipt'}
                    {' '} — This format will be pre-selected whenever you click Print on any invoice.
                  </div>
                </div>

                <SaveRow acc={acc} saving={saving} saved={saved} onSave={() => save({ print_format: form.print_format, page_size: form.page_size, page_orientation: form.page_orientation, margin_top: parseFloat(form.margin_top) || 10, margin_bottom: parseFloat(form.margin_bottom) || 10, margin_left: parseFloat(form.margin_left) || 10, margin_right: parseFloat(form.margin_right) || 10 }, 'Print Settings Saved')} />

                {/* Page Settings */}
                <div style={{ marginTop: 32, paddingTop: 24, borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                  <h3 style={{ ...pg, fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>📐 Page Settings</h3>
                  <p style={{ ...muted, fontSize: 13, margin: '0 0 18px' }}>Configure page size and margins for printed invoices.</p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 20 }}>
                    <Field lbl={lbl} label="Page Size">
                      <select style={inp} value={form.page_size} onChange={e => setForm(prev => ({ ...prev, page_size: e.target.value }))}>
                        <option value="A4">A4 (210×297mm)</option>
                        <option value="A5">A5 (148×210mm)</option>
                        <option value="Letter">Letter (8.5×11in)</option>
                        <option value="Legal">Legal (8.5×14in)</option>
                      </select>
                    </Field>
                    <Field lbl={lbl} label="Orientation">
                      <select style={inp} value={form.page_orientation} onChange={e => setForm(prev => ({ ...prev, page_orientation: e.target.value }))}>
                        <option value="portrait">Portrait</option>
                        <option value="landscape">Landscape</option>
                      </select>
                    </Field>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 16, marginBottom: 20 }}>
                    <Field lbl={lbl} label="Top Margin (mm)">
                      <input style={inp} type="number" min="0" max="50" step="1" value={form.margin_top} onChange={e => setForm(prev => ({ ...prev, margin_top: e.target.value }))} />
                    </Field>
                    <Field lbl={lbl} label="Bottom Margin (mm)">
                      <input style={inp} type="number" min="0" max="50" step="1" value={form.margin_bottom} onChange={e => setForm(prev => ({ ...prev, margin_bottom: e.target.value }))} />
                    </Field>
                    <Field lbl={lbl} label="Left Margin (mm)">
                      <input style={inp} type="number" min="0" max="50" step="1" value={form.margin_left} onChange={e => setForm(prev => ({ ...prev, margin_left: e.target.value }))} />
                    </Field>
                    <Field lbl={lbl} label="Right Margin (mm)">
                      <input style={inp} type="number" min="0" max="50" step="1" value={form.margin_right} onChange={e => setForm(prev => ({ ...prev, margin_right: e.target.value }))} />
                    </Field>
                  </div>

                  <div style={{ padding: '12px 16px', background: darkMode ? '#0F172A' : '#f8fafc', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
                    <div style={{ fontSize: 12, ...muted }}>
                      <strong>Preview:</strong> {form.page_size} {form.page_orientation} — Margins: {form.margin_top}mm (top), {form.margin_bottom}mm (bottom), {form.margin_left}mm (left), {form.margin_right}mm (right)
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Backup & Restore ── */}
            {activeTab === 'backup' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* ── Status Messages ── */}
                {backupMsg && (
                  <div style={{ padding: '12px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    background: backupMsg.startsWith('✅') ? (darkMode ? '#0d2b1e' : '#f0fdf4') : (darkMode ? '#2d1515' : '#fef2f2'),
                    color: backupMsg.startsWith('✅') ? (darkMode ? '#86efac' : '#15803d') : (darkMode ? '#fca5a5' : '#dc2626'),
                    border: `1px solid ${backupMsg.startsWith('✅') ? (darkMode ? '#16a34a44' : '#86efac') : (darkMode ? '#ef444444' : '#fca5a5')}` }}>
                    {backupMsg}
                  </div>
                )}

                {/* ── Restore Confirmation Dialog ── */}
                {showRestoreConfirm && (
                  <div style={{ padding: '24px', background: darkMode ? '#2d1515' : '#fef2f2', borderRadius: 12, border: `2px solid ${darkMode ? '#ef4444' : '#fca5a5'}` }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: darkMode ? '#fca5a5' : '#dc2626', marginBottom: 10 }}>⚠️ Confirm Data Restore</div>
                    <p style={{ fontSize: 13, color: darkMode ? '#f1f5f9' : '#1e293b', margin: '0 0 8px', lineHeight: 1.6 }}>
                      This will <strong>replace ALL your current data</strong> with the backup file:
                    </p>
                    <div style={{ padding: '8px 14px', background: darkMode ? '#1e293b' : '#fff', borderRadius: 8, fontSize: 13, color: darkMode ? '#94a3b8' : '#475569', marginBottom: 14 }}>
                      📁 {restoreFile?.name} ({(restoreFile?.size / 1024).toFixed(1)} KB)
                    </div>
                    <p style={{ fontSize: 12, color: darkMode ? '#f87171' : '#b91c1c', margin: '0 0 18px', lineHeight: 1.5 }}>
                      All existing customers, products, invoices, expenses, and other data will be replaced.
                      Your passwords and login credentials will be preserved. This action cannot be undone.
                    </p>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button onClick={executeRestore} disabled={restoring}
                        style={{ padding: '10px 24px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                        {restoring ? '⏳ Restoring…' : '⚠️ Yes, Restore Now'}
                      </button>
                      <button onClick={cancelRestore}
                        style={{ padding: '10px 24px', background: darkMode ? '#334155' : '#e2e8f0', color: darkMode ? '#f1f5f9' : '#1e293b', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Section 1: Download Backup ── */}
                <div style={{ ...card, padding: 28 }}>
                  <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>💾 Download Backup</h3>
                  <p style={{ ...muted, fontSize: 13, margin: '0 0 18px' }}>
                    Download a complete backup of all your shop data — customers, products, invoices, payments, expenses, stock, suppliers, and everything else. Keep it safe!
                  </p>
                  <button onClick={downloadBackupNow} disabled={backupLoading || restoring}
                    style={{ padding: '10px 24px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                    {backupLoading ? '⏳ Generating…' : '⬇️ Download Latest Backup'}
                  </button>

                  {/* Backup history */}
                  {backupList.length > 0 && (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recent Server Backups</div>
                      <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
                        {backupList.slice(0, 10).map((bk, i) => (
                          <div key={bk.filename} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px',
                            background: i % 2 === 0 ? (darkMode ? '#0f172a' : '#f8fafc') : (darkMode ? '#1e293b' : '#fff'),
                            borderBottom: i < Math.min(backupList.length, 10) - 1 ? `1px solid ${darkMode ? '#1e293b' : '#f1f5f9'}` : 'none' }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: darkMode ? '#f1f5f9' : '#1e293b' }}>{bk.filename}</div>
                              <div style={{ fontSize: 11, color: darkMode ? '#64748b' : '#94a3b8', marginTop: 2 }}>
                                {formatDateDMY(bk.created_at, '—')} · {bk.size_kb} KB
                              </div>
                            </div>
                            <button onClick={() => downloadBackupFile(bk.filename)}
                              disabled={fileDownloading[bk.filename] || restoring}
                              style={{ padding: '5px 14px', background: darkMode ? '#334155' : '#e2e8f0', color: darkMode ? '#f1f5f9' : '#1e293b', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
                              {fileDownloading[bk.filename] ? '⏳…' : '⬇️ Download'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Section 2: Restore from Backup ── */}
                <div style={{ ...card, padding: 28 }}>
                  <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>🔄 Restore from Backup</h3>
                  <p style={{ ...muted, fontSize: 13, margin: '0 0 18px' }}>
                    Upload a backup file (.json.gz) to restore your shop data to a previous state.
                    Use this if data was accidentally deleted, wrongly edited by staff, or lost due to any issue.
                  </p>

                  <div style={{ padding: '20px', background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10, border: `2px dashed ${darkMode ? '#475569' : '#cbd5e1'}`, textAlign: 'center', marginBottom: 16, position: 'relative' }}>
                    <input type="file" accept=".json.gz,.gz" onChange={handleRestoreUpload} disabled={restoring}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: darkMode ? '#f1f5f9' : '#1e293b', marginBottom: 4 }}>
                      {restoring ? '⏳ Restoring data… please wait' : 'Click or drag a backup file here'}
                    </div>
                    <div style={{ fontSize: 12, color: darkMode ? '#64748b' : '#94a3b8' }}>
                      Accepts .json.gz backup files (downloaded from here or from Google Drive)
                    </div>
                  </div>

                  <div style={{ padding: '12px 16px', background: darkMode ? '#1e293b' : '#fffbeb', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#fde68a'}`, fontSize: 12, color: darkMode ? '#94a3b8' : '#92400e', lineHeight: 1.6 }}>
                    <strong>⚠️ Important:</strong> Restore replaces ALL your current data with the backup. Your login passwords, API tokens, and Google Drive connection will be preserved. Only your own shop's data is affected — other shops are not touched.
                  </div>
                </div>

                {/* ── Section 3: Google Drive Backup ── */}
                <div style={{ ...card, padding: 28 }}>
                  <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>☁️ Google Drive Backup</h3>
                  <p style={{ ...muted, fontSize: 13, margin: '0 0 24px' }}>
                    Automatically backup all your shop data to Google Drive every day at 2 AM.
                    Your data stays safe even if something goes wrong with the server.
                  </p>

                  {/* How-to instructions */}
                  <div style={{ padding: '14px 18px', background: darkMode ? '#1e293b' : '#f0f9ff', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#bae6fd'}`, marginBottom: 24 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: darkMode ? '#38bdf8' : '#0369a1', marginBottom: 8 }}>📋 One-time setup (5 minutes):</div>
                    <ol style={{ margin: '0 0 0 18px', padding: 0, fontSize: 13, color: darkMode ? '#94a3b8' : '#475569', lineHeight: 2 }}>
                      <li>Go to <strong>console.cloud.google.com</strong> → Create a new project</li>
                      <li>Enable the <strong>Google Drive API</strong> (APIs &amp; Services → Library)</li>
                      <li>Go to <strong>OAuth consent screen</strong> → User type: External → fill App name &amp; your email → Save &amp; continue. Under <strong>Test users → + Add users</strong> → add your Gmail address. <span style={{ color: darkMode?'#f87171':'#dc2626', fontWeight:600 }}>Required — skipping this causes Error 403 access_denied.</span></li>
                      <li>Create <strong>OAuth 2.0 credentials</strong> (Credentials → Create Credentials → OAuth client ID → Web application)</li>
                      <li>Add Authorized redirect URI: <span style={{ display:'inline-flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                          <code style={{ background: darkMode ? '#0f172a' : '#e0f2fe', padding: '2px 8px', borderRadius: 4, fontSize: 12, userSelect:'all' }}>{gdriveStatus.redirect_uri || (window.location.origin + '/api/settings/gdrive/callback')}</code>
                          <button onClick={() => { const uri = gdriveStatus.redirect_uri || (window.location.origin + '/api/settings/gdrive/callback'); navigator.clipboard?.writeText(uri); }} style={{ padding:'2px 10px', fontSize:11, background:'transparent', border:`1px solid ${darkMode?'#475569':'#94a3b8'}`, borderRadius:4, cursor:'pointer', color: darkMode?'#94a3b8':'#64748b' }}>📋 Copy</button>
                        </span></li>
                      <li>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong> and paste below</li>
                    </ol>
                  </div>

                  {/* Credentials form */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Field lbl={lbl} label="Google OAuth Client ID">
                          <input style={{...inp, fontFamily:'monospace', fontSize:12}} type="text" value={gdriveConfig.client_id}
                          onChange={e => setGdriveConfig(c => ({ ...c, client_id: e.target.value }))}
                            placeholder="123456789-abcdef.apps.googleusercontent.com" autoComplete="off" spellCheck={false} />
                      </Field>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Field lbl={lbl} label="Google OAuth Client Secret">
                          <input style={{...inp, fontFamily:'monospace', fontSize:12}} type="text" value={gdriveConfig.client_secret}
                          onChange={e => setGdriveConfig(c => ({ ...c, client_secret: e.target.value }))}
                            placeholder={gdriveStatus.credentials_set ? '(leave blank to keep current)' : 'GOCSPX-…'} autoComplete="off" spellCheck={false} />
                      </Field>
                    </div>
                    <div>
                      <Field lbl={lbl} label="Drive Folder ID (optional — leave blank to auto-create separate backup folder)">
                        <input style={inp} value={gdriveConfig.folder_id}
                          onChange={e => setGdriveConfig(c => ({ ...c, folder_id: e.target.value }))}
                          placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms" />
                      </Field>
                    </div>
                  </div>
                  <button onClick={saveGdriveConfig} disabled={gdriveLoading || (!gdriveConfig.client_id && !gdriveConfig.client_secret && !gdriveConfig.folder_id)}
                    style={{ padding: '9px 22px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13, marginBottom: 24 }}>
                    {gdriveLoading ? '⏳ Saving…' : '💾 Save Credentials'}
                  </button>

                  {/* Connect / Status section */}
                  {gdriveStatus.credentials_set && !gdriveStatus.connected && (
                    <div style={{ padding: '20px 24px', background: darkMode ? '#1e293b' : '#fafafa', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, marginBottom: 24 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: darkMode ? '#f1f5f9' : '#0f172a', marginBottom: 10 }}>🔗 Connect your Google Account</div>
                      <p style={{ ...muted, fontSize: 13, margin: '0 0 16px' }}>Click the button below and sign in with the Google account whose Drive you want to use for backups.</p>
                      <button onClick={connectGDrive} disabled={gdriveLoading}
                        style={{ padding: '10px 24px', background: '#4285F4', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                        🔗 Connect Google Drive
                      </button>
                    </div>
                  )}

                  {gdriveStatus.connected && (
                    <div style={{ padding: '20px 24px', background: darkMode ? '#0d2b1e' : '#f0fdf4', borderRadius: 10, border: `1px solid ${darkMode ? '#16a34a44' : '#86efac'}`, marginBottom: 24 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                        <span style={{ fontSize: 20 }}>✅</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: darkMode ? '#86efac' : '#15803d' }}>Google Drive Connected</div>
                          <div style={{ fontSize: 12, color: darkMode ? '#64748b' : '#6b7280', marginTop: 2 }}>
                            Last backup: {gdriveStatus.last_backup ? formatDateDMY(gdriveStatus.last_backup, '—') : 'Never'}
                            {gdriveStatus.folder_id && ` · Folder: ${gdriveStatus.folder_id.slice(0, 12)}…`}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button onClick={runBackupNow} disabled={gdriveLoading}
                          style={{ padding: '9px 20px', background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                          {gdriveLoading ? '⏳ Running…' : '⬆️ Backup Now'}
                        </button>
                        <button onClick={disconnectGDrive} disabled={gdriveLoading}
                          style={{ padding: '9px 20px', background: 'transparent', color: darkMode ? '#ef4444' : '#dc2626', border: `1px solid ${darkMode ? '#ef444444' : '#fca5a5'}`, borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                          🔌 Disconnect
                        </button>
                      </div>
                    </div>
                  )}

                  {gdriveMsg && (
                    <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
                      background: gdriveMsg.startsWith('✅') ? (darkMode ? '#0d2b1e' : '#f0fdf4') : (darkMode ? '#2d1515' : '#fef2f2'),
                      color: gdriveMsg.startsWith('✅') ? (darkMode ? '#86efac' : '#15803d') : (darkMode ? '#fca5a5' : '#dc2626'),
                      border: `1px solid ${gdriveMsg.startsWith('✅') ? (darkMode ? '#16a34a44' : '#86efac') : (darkMode ? '#ef444444' : '#fca5a5')}` }}>
                      {gdriveMsg}
                    </div>
                  )}

                  <div style={{ padding: '12px 16px', background: darkMode ? '#1e293b' : '#fffbeb', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#fde68a'}`, fontSize: 12, color: darkMode ? '#94a3b8' : '#92400e' }}>
                    <strong>🔒 Privacy note:</strong> Only your shop's own data is backed up (settings, customers, products, invoices).
                    Sensitive credentials (passwords, tokens) are excluded from the backup file.
                    Backups run automatically every day at 2:00 AM (server time).
                  </div>
                </div>
              </div>
            )}

            {/* ── Change Password ── */}
            {activeTab === 'password' && (
              <div style={{ ...card, padding: 28 }}>
                <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>🔒 Change Password</h3>
                <p style={{ ...muted, fontSize: 13, margin: '0 0 22px' }}>Update your login password. You'll need to enter your current password for verification.</p>

                {pwMsg && (
                  <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
                    background: pwMsg.startsWith('✅') ? (darkMode ? '#0d2b1e' : '#f0fdf4') : (darkMode ? '#2d1515' : '#fef2f2'),
                    color: pwMsg.startsWith('✅') ? (darkMode ? '#86efac' : '#15803d') : (darkMode ? '#fca5a5' : '#dc2626'),
                    border: `1px solid ${pwMsg.startsWith('✅') ? (darkMode ? '#16a34a44' : '#86efac') : (darkMode ? '#ef444444' : '#fca5a5')}` }}>
                    {pwMsg}
                  </div>
                )}

                <div style={{ display: 'grid', gap: 18, maxWidth: 400 }}>
                  <Field lbl={lbl} label="Current Password">
                    <input style={inp} type="password" value={pwForm.current}
                      onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} placeholder="Enter current password" />
                  </Field>
                  <Field lbl={lbl} label="New Password">
                    <input style={inp} type="password" value={pwForm.newPw}
                      onChange={e => setPwForm(f => ({ ...f, newPw: e.target.value }))} placeholder="Min 6 characters" />
                  </Field>
                  <Field lbl={lbl} label="Confirm New Password">
                    <input style={inp} type="password" value={pwForm.confirm}
                      onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Re-enter new password" />
                  </Field>
                </div>

                <div style={{ marginTop: 24 }}>
                  <button type="button" disabled={pwSaving}
                    onClick={async () => {
                      setPwMsg('');
                      if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) { setPwMsg('❌ All fields are required'); return; }
                      if (pwForm.newPw.length < 6) { setPwMsg('❌ New password must be at least 6 characters'); return; }
                      if (pwForm.newPw !== pwForm.confirm) { setPwMsg('❌ New passwords do not match'); return; }
                      setPwSaving(true);
                      try {
                        await authAPI.changePassword(pwForm.current, pwForm.newPw);
                        setPwMsg('✅ Password changed successfully!');
                        setPwForm({ current: '', newPw: '', confirm: '' });
                      } catch (e) { setPwMsg('❌ ' + (e.response?.data?.detail || e.message)); }
                      setPwSaving(false);
                    }}
                    style={{ padding: '10px 28px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: pwSaving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                    {pwSaving ? '⏳ Changing...' : '🔒 Change Password'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Theme ── */}
            {activeTab === 'theme' && (
              <div style={{ ...card, padding: 28 }}>
                <h3 style={{ ...pg, fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>🎨 Theme & Appearance</h3>
                <p style={{ ...muted, fontSize: 13, margin: '0 0 22px' }}>Customize the look and feel of your dashboard.</p>

                <div style={{ marginBottom: 28 }}>
                  <label style={lbl}>Color Theme</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                    {themes && Object.entries(themes).map(([key, t]) => (
                      <button key={key} onClick={() => setColor(key)} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderRadius: 12,
                        border: colorKey === key ? `2px solid ${t.swatch}` : `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
                        background: colorKey === key ? (darkMode ? `${t.swatch}22` : `${t.swatch}11`) : (darkMode ? '#0F172A' : '#F8FAFC'),
                        cursor: 'pointer', transition: 'all 0.2s',
                      }}>
                        <span style={{ width: 24, height: 24, borderRadius: '50%', background: t.swatch, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 700, color: colorKey === key ? t.swatch : (darkMode ? '#94A3B8' : '#64748B') }}>{t.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={lbl}>Dark Mode</label>
                  <button onClick={toggleDark} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 24px', borderRadius: 12, marginTop: 8,
                    border: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
                    background: darkMode ? '#1E293B' : '#F8FAFC', cursor: 'pointer', transition: 'all 0.2s',
                  }}>
                    <span style={{ fontSize: 20 }}>{darkMode ? '🌙' : '☀️'}</span>
                    <div style={{ textAlign: 'left' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: darkMode ? '#F1F5F9' : '#0F172A', display: 'block' }}>
                        {darkMode ? 'Dark Mode' : 'Light Mode'}
                      </span>
                      <span style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8' }}>
                        {darkMode ? 'Click to switch to light mode' : 'Click to switch to dark mode'}
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Settings;
